import * as Yup from "yup";
import { dateToyyyyMMdd } from "../../../util/date";
import { getPatchOperations } from "../../../util/form";
import { validationMessages } from "../../../constants/validation";
import {
  MakeOptional,
  PersonKlienterklaering,
} from "../../../interfaces/schema";
import AutoCompleteFormular from "../../../components/autocomplete/AutoCompleteFormular";

export const personKlienterklaeringForm = (
  personId: string,
  klienterklaeringId?: string,
  kke?: PersonKlienterklaering,
): FormConfig<PersonKlienterklaeringDTO> => {
  const isLocked = !!kke?.erklModtaget;
  return {
    title: "klientkontoerklæring",
    lockedValues: [
      {
        id: "personId",
        value: personId,
      },
      {
        id: "klienterklaeringId",
        value: klienterklaeringId,
      },
    ],
    validationSchema: klientkontoerklaeringValidationSchema,
    form: [
      {
        component: "textinput",
        id: "erklAar",
        label: "Erklæringsår",
        locked: true,
        initialValue: "",
        isRequired: true,
      },
      {
        component: "kode-select",
        kodeListeType: "KlienterklaeringstypeKode",
        id: "klienterklaeringstype",
        label: "Type",
        placeholder: "Vælg type",
        locked: true,
        initialValue: "",
        isRequired: true,
      },
      {
        component: "textinput",
        id: "firmaCvrNr",
        label: "CVR firma",
        locked: true,
        initialValue: "",
      },
      {
        component: "kode-select",
        kodeListeType: "KlienterklaeringStatusKode",
        id: "klienterklaeringStatus",
        label: "Status",
        placeholder: "Vælg status",
        locked: false,
        initialValue: "",
        isRequired: true,
        filter: isLocked ? ["FristOrdinaer"] : undefined,
      },
      {
        component: "custom",
        id: "indberettetformular",
        label: "Formular",
        initialValue: "Formular1",
        locked: isLocked,
        isRequired: true,
        conditionalHidden: ({ values }) =>
          values.klienterklaeringStatus === "FristOrdinaer" ||
          values.klienterklaeringStatus === "",
        customComponent: ({ formik }) => (
          <AutoCompleteFormular
            onSelectedItemEvent={(item) =>
              formik.setFieldValue(
                "indberettetformular",
                item.selectedItem?.id,
                true,
              )
            }
            isLocked={isLocked}
            initialValue={formik.values.indberettetformular}
            label="Formular"
            formik={formik}
            id="indberettetformular"
            isRequired
          />
        ),
      },
      {
        component: "datepicker",
        id: "erklModtaget",
        label: "Modtaget dato",
        locked: isLocked,
        initialValue: "",
        isRequired: true,
        conditionalHidden: ({ values }) =>
          values.klienterklaeringStatus === "FristOrdinaer" ||
          values.klienterklaeringStatus === "",
      },
      {
        component: "textinput",
        id: "erklNr",
        label: "Nr. analog erklæring",
        locked: isLocked,
        initialValue: "",
        conditionalHidden: ({ values }) =>
          values.klienterklaeringStatus === "FristOrdinaer" ||
          values.klienterklaeringStatus === "",
      },
      {
        component: "datepicker",
        id: "periodeFraDato",
        label: "Startdato erklæringsperiode",
        locked: true,
        initialValue: "",
        isRequired: true,
      },
      {
        component: "datepicker",
        id: "periodeTilDatoInklusiv",
        label: "Slutdato erklæringsperiode",
        locked: true,
        initialValue: "",
        isRequired: true,
      },
      {
        component: "datepicker",
        id: "frist",
        label: "Frist",
        locked: true,
        isRequired: true,
        initialValue: "",
      },
    ],
    get: `person/${personId}/klienterklaeringer/${klienterklaeringId}`,
    getDocument: `person/${personId}/klienterklaeringer/${klienterklaeringId}/dokument`,
    downloadLink: `person/${personId}/klienterklaeringer/${klienterklaeringId}/download`,
    patch(values) {
      const isFristOrdinaer = values.klienterklaeringStatus === "FristOrdinaer";
      if (!isLocked && !isFristOrdinaer && !values.indberettetformular) {
        values.indberettetformular = "Formular1";
      }
      if (!values.klienterklaeringStatus) {
        delete (
          values as MakeOptional<PersonKlienterklaeringDTO, "erklModtaget">
        ).erklModtaget;
        delete (
          values as MakeOptional<
            PersonKlienterklaeringDTO,
            "indberettetformular"
          >
        ).indberettetformular;
        delete (values as MakeOptional<PersonKlienterklaeringDTO, "erklNr">)
          .erklNr;
      }
      return {
        query: `person/${personId}/klienterklaeringer/${klienterklaeringId}`,
        body: JSON.stringify(getPatchOperations(values)),
      };
    },
    post(values) {
      const {
        erklAar,
        erklModtaget,
        erklNr,
        firmaCvrNr,
        klienterklaeringStatus,
        klienterklaeringstype,
        periodeFraDato,
        periodeTilDatoInklusiv,
        indberettetformular,
        frist,
      } = values;
      const isFristOrdinaer = klienterklaeringStatus === "FristOrdinaer";
      return {
        query: `person/${personId}/klienterklaeringer/`,
        body: {
          erklAar: erklAar ? erklAar.toString() : null,
          erklModtaget:
            erklModtaget && !isFristOrdinaer
              ? dateToyyyyMMdd(erklModtaget)
              : null,
          erklNr: erklNr ? erklNr : null,
          firmaCvrNr: firmaCvrNr ? firmaCvrNr : null,
          klienterklaeringStatus: klienterklaeringStatus.toString(),
          klienterklaeringstype: klienterklaeringstype.toString(),
          periodeFraDato: dateToyyyyMMdd(periodeFraDato),
          periodeTilDatoInklusiv: dateToyyyyMMdd(periodeTilDatoInklusiv),
          indberettetformular: isFristOrdinaer
            ? null
            : indberettetformular
              ? "Formular1"
              : "Formular2",
          frist: dateToyyyyMMdd(frist),
        },
      };
    },
    delete() {
      return {
        query: `person/${personId}/klienterklaeringer/${klienterklaeringId}`,
      };
    },
  };
};

export const klientkontoerklaeringValidationSchema = Yup.object({
  erklAar: Yup.number()
    .required("År skal være sat")
    .typeError("Opkrævningsår skal være et tal")
    .test(
      "len",
      "Ugyldigt opkrævningsår",
      (value) => value?.toString().length === 4,
    )
    .min(1950, "For lavt år")
    .max(new Date().getFullYear(), "Må ikke være i fremtiden"),
  klienterklaeringstype: Yup.string().required("Vælg erklæringstype"),
  klienterklaeringStatus: Yup.string().required("Vælg erklæringsstatus"),
  firmaCvrNr: Yup.number().typeError("Skal være et tal"),
  erklModtaget: Yup.date()
    .nullable()
    .when("klienterklaeringStatus", {
      is: (value: string) => value !== "FristOrdinaer",
      then: (schema) => schema.required(validationMessages.required),
    }),
  erklNr: Yup.string(),
  periodeFraDato: Yup.date().nullable().required("Startdato skal være sat"),
  periodeTilDatoInklusiv: Yup.date()
    .nullable()
    .when(
      "periodeFraDato",
      (periodeFraDato, yup) =>
        periodeFraDato &&
        yup.min(periodeFraDato, "Slutdato kan ikke være før startdato"),
    )
    .required("Slutdato skal være sat"),
  indberettetformular: Yup.string().when("klienterklaeringStatus", {
    is: (value: string) => value !== "FristOrdinaer",
    then: (schema) => schema.required(validationMessages.required),
  }),
  frist: Yup.date().nullable().required(validationMessages.required),
});
